<script setup>
import { computed, ref } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { MESSAGE_STATUS } from './constants';

import Icon from 'next/icon/Icon.vue';

const { status, onMedia } = defineProps({
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  onMedia: { type: Boolean, default: false },
});

const { t } = useI18n();

const progresIconSequence = [
  'i-lucide-clock-1',
  'i-lucide-clock-2',
  'i-lucide-clock-3',
  'i-lucide-clock-4',
  'i-lucide-clock-5',
  'i-lucide-clock-6',
  'i-lucide-clock-7',
  'i-lucide-clock-8',
  'i-lucide-clock-9',
  'i-lucide-clock-10',
  'i-lucide-clock-11',
  'i-lucide-clock-12',
];

const progessIcon = ref(progresIconSequence[0]);

const rotateIcon = () => {
  const currentIndex = progresIconSequence.indexOf(progessIcon.value);
  const nextIndex = (currentIndex + 1) % progresIconSequence.length;
  progessIcon.value = progresIconSequence[nextIndex];
};

useIntervalFn(rotateIcon, 500, {
  immediate: status === MESSAGE_STATUS.PROGRESS,
  immediateCallback: false,
});

// Aceleres 2026-08-19 -- os tiques deixaram de ser icones do lucide.
//
// O `i-lucide-check-check` desenha DOIS tiques de tamanhos diferentes, o menor
// encaixado dentro do maior, numa caixa quadrada de 14x14. Nada disso e o
// WhatsApp: la os dois tiques sao iguais, o de tras aparece so em parte atras
// do da frente, e a caixa e deitada (16x11), o que deixa o par baixo e largo
// ao lado do horario em vez de alto e apertado.
//
// A geometria mora aqui, em duas constantes, e nao no template: o tique da
// frente e o MESMO desenho no estado enviado e no entregue/lido — o que muda e
// so a presenca do de tras. Escrever o caminho duas vezes no template faria
// duas fontes para a mesma forma, e um retoque futuro acertaria so uma.
//
// Os dois tiques sao o MESMO desenho deslocado 4 para a esquerda, como no
// sprite do WhatsApp: nenhum e cortado, o de tras passa por tras do da frente e
// o cruzamento e o que da a silhueta. Encurtar o de tras (a primeira tentativa
// aqui) transforma o par num "W" com um toco no meio.
const TICK = 'M5.8 6.1 8.2 8.4 14.6 1';
const TICK_BACK = 'M1.8 6.1 4.2 8.4 10.6 1';

const hasDoubleTick = computed(() =>
  [MESSAGE_STATUS.DELIVERED, MESSAGE_STATUS.READ].includes(status)
);

// Com o tique simples a caixa e recortada em cima do MESMO desenho — a janela
// abre em x=4.4, que e onde o tique da frente comeca (com o meio traco de
// folga) — em vez de o caminho ser reescrito centrado. A largura em px
// acompanha a da janela para a escala continuar 1:1: se divergirem, o traco do
// tique simples engorda em relacao ao do duplo e os dois estados param de
// combinar.
const viewBox = computed(() =>
  hasDoubleTick.value ? '0 0 16 11' : '4.4 0 11.6 11'
);
const width = computed(() => (hasDoubleTick.value ? 16 : 11.6));

// O azul do lido e o do WhatsApp (#53BDEB). O #7EB6FF anterior era mais claro
// e mais roxo, e sumia contra o verde da bolha enviada. Enviado e entregue
// seguem no token de cinza, que e quem acompanha o tema claro/escuro.
const statusColor = computed(() => {
  if (onMedia) return 'text-white';

  const statusIconMap = {
    [MESSAGE_STATUS.SENT]: 'text-n-slate-10',
    [MESSAGE_STATUS.DELIVERED]: 'text-n-slate-10',
    [MESSAGE_STATUS.READ]: 'text-[#53BDEB]',
  };

  return statusIconMap[status];
});

const tooltipText = computed(() => {
  const statusTextMap = {
    [MESSAGE_STATUS.SENT]: t('CHAT_LIST.SENT'),
    [MESSAGE_STATUS.DELIVERED]: t('CHAT_LIST.DELIVERED'),
    [MESSAGE_STATUS.READ]: t('CHAT_LIST.MESSAGE_READ'),
    [MESSAGE_STATUS.PROGRESS]: t('CHAT_LIST.SENDING'),
  };

  return statusTextMap[status];
});
</script>

<template>
  <Icon
    v-if="status === MESSAGE_STATUS.PROGRESS"
    v-tooltip.top-start="tooltipText"
    :icon="progessIcon"
    :class="onMedia ? 'text-white' : 'text-n-slate-10'"
  />
  <svg
    v-else
    v-tooltip.top-start="tooltipText"
    :class="statusColor"
    :viewBox="viewBox"
    :width="width"
    height="11"
    fill="none"
    stroke="currentColor"
    stroke-width="1.3"
    stroke-linecap="round"
    stroke-linejoin="round"
    role="img"
    :aria-label="tooltipText"
    class="shrink-0"
  >
    <path v-if="hasDoubleTick" :d="TICK_BACK" />
    <path :d="TICK" />
  </svg>
</template>
